import { useTranslation } from "react-i18next";
import type { SocialCommentListItemDto } from "@multi-chat/shared";
import { Image as ImageIcon } from "lucide-react";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { cn } from "../../../lib/utils.js";
import { formatConversationFullDate, formatConversationListTime } from "../conversation-time.js";
import {
  isBusinessSelfAuthor,
  resolveCommentAuthorLabel,
} from "./comment-identity.js";

export function CommentRowItem({
  item,
  selected,
  nowMs,
  onSelect,
}: {
  item: SocialCommentListItemDto;
  selected: boolean;
  nowMs: number;
  onSelect: (commentId: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const identity = resolveCommentAuthorLabel({
    authorDisplayName: item.authorDisplayName,
    authorUsername: item.authorUsername,
    unknownLabel: t("inbox.comments.author.unknown"),
  });
  const businessSelf = isBusinessSelfAuthor(item.authorType);
  const unread = !item.isRead && item.handlingStatus === "OPEN";
  const time = formatConversationListTime(item.lastActivityAt, i18n.language, nowMs, t);
  const fullTime = formatConversationFullDate(item.lastActivityAt, i18n.language);
  const handled = item.handlingStatus === "HANDLED";

  return (
    <button
      type="button"
      data-comment-id={item.id}
      className={cn(
        "relative isolate mx-1.5 my-0.5 flex min-h-14 w-[calc(100%-0.75rem)] cursor-pointer items-start gap-2 rounded-lg px-2 py-2 text-start text-inherit transition-colors",
        "hover:bg-surface-muted/80",
        "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-primary",
        selected &&
          "bg-brand-mint/40 after:absolute after:start-0 after:top-2 after:bottom-2 after:w-[3px] after:rounded-e-[3px] after:bg-primary",
      )}
      onClick={() => onSelect(item.id)}
    >
      <span className="relative mt-0.5 shrink-0 overflow-hidden rounded-md bg-surface-muted">
        {item.postMediaPreviewUrl ? (
          <img
            src={item.postMediaPreviewUrl}
            alt=""
            className="size-9 object-cover"
            draggable={false}
          />
        ) : (
          <span className="grid size-9 place-items-center text-muted">
            <ImageIcon size={14} aria-hidden="true" />
          </span>
        )}
        <span className="absolute -bottom-0.5 -end-0.5">
          <ChannelIdentityBadge platformType={item.platformType} size="inline" />
        </span>
        {unread ? (
          <span
            className="absolute -top-0.5 -start-0.5 size-2 rounded-full bg-primary ring-2 ring-surface"
            aria-hidden="true"
          />
        ) : null}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-[0.84375rem] leading-tight text-inbox-strong",
              unread ? "font-[640]" : "font-[530]",
            )}
            dir="auto"
          >
            {identity.isHandleOnly ? (
              <LtrValue className="truncate">{identity.primary}</LtrValue>
            ) : (
              identity.primary
            )}
            {businessSelf ? (
              <span className="ms-1 font-medium text-inbox-muted">
                {t("inbox.comments.author.meSuffix")}
              </span>
            ) : null}
          </span>
          <span
            className={cn(
              "shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold",
              handled
                ? "bg-inbox-line-soft text-inbox-muted"
                : "bg-brand-mint text-primary",
            )}
          >
            {handled
              ? t("inbox.comments.status.handled")
              : t("inbox.comments.status.open")}
          </span>
          {time ? (
            <time
              className={cn(
                "shrink-0 text-[0.6875rem] tabular-nums",
                unread ? "font-[560] text-primary" : "text-inbox-subtle",
              )}
              dateTime={item.lastActivityAt}
              title={fullTime || undefined}
            >
              <LtrValue>{time}</LtrValue>
            </time>
          ) : null}
        </span>

        <span
          className={cn(
            "min-w-0 truncate text-[0.78125rem] leading-snug",
            unread ? "text-[#33463f]" : "text-inbox-muted",
          )}
          dir="auto"
        >
          {item.content || t("inbox.comments.emptyBody")}
        </span>

        {item.postCaptionPreview ? (
          <span className="min-w-0 truncate text-[0.6875rem] text-inbox-subtle" dir="auto">
            {t("inbox.comments.onPost", { caption: item.postCaptionPreview })}
          </span>
        ) : null}
      </span>
    </button>
  );
}
